import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { getDashboardData, getMonthlyLoadSummary } from 'apis/adminServices/dashboardService';
import { useGlobalLoader } from 'contexts/GlobalLoaderContext';
import { Link } from 'react-router-dom';
import { useUserStore } from 'store/userStore';



const AdminDashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [monthlyData, setMonthlyData] = useState<any>([]);
  const { showLoader, hideLoader } = useGlobalLoader();
  const { username, profilePictureUrl } = useUserStore(state => state);

  useEffect(() => {
    showLoader();
    const fetchData = async () => {
      try {
        const data = await getDashboardData();
        setDashboardData(data);
        showLoader();
        const currentYear = new Date().getFullYear(); // Get current year dynamically
        const monthlyLoadData = await getMonthlyLoadSummary(currentYear); // Pass year here

        const formattedData = monthlyLoadData.map((item: any) => ({
          month: item.month.substring(0, 3), // Shorten month name to 3 characters
          Posted: item.loadsPosted,
          Answered: item.loadsAnswered
        }));

        setMonthlyData(formattedData);
      } catch (err) {
        console.error('Error loading dashboard data', err);
      } finally {
        hideLoader();
      }
    };

    fetchData();
  }, []);

  // Pie chart data
  const loadDistributionData = [
    { name: 'Posted', value: dashboardData?.loadPostedCount || 0 },
    { name: 'Answered', value: dashboardData?.loadAnsweredCount || 0 }
  ];

  const renderCustomizedLabel = (props: any) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, outerRadius, payload } = props;

    // Calculate the position for the external label
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);

    // Position the external label just outside the (smaller) pie.
    const labelRadius = outerRadius * 1.15;
    const x = cx + labelRadius * cos;
    const y = cy + labelRadius * sin;

    // Position for the connector dot - start further inside the pie
    const dotX = cx + outerRadius * 0.7 * cos;
    const dotY = cy + outerRadius * 0.7 * sin;

    // Determine box position based on which half of the pie
    const isRightSide = cos > 0;
    const boxWidth = 84;
    const boxHeight = 46;

    // Clamp the label box inside the chart's SVG (width = 2*cx, height = 2*cy)
    // so it's never clipped by the narrow container edge, whatever the angle.
    let boxX = isRightSide ? x : x - boxWidth;
    boxX = Math.max(4, Math.min(boxX, cx * 2 - boxWidth - 4));
    const boxY = Math.max(4, Math.min(y - boxHeight / 2, cy * 2 - boxHeight - 4));

    return (
      <g>
        {/* Connector line */}
        <line
          x1={dotX}
          y1={dotY}
          x2={x}
          y2={y}
          stroke="#000"
          strokeWidth={1}
        />

        {/* Connector dot */}
        <circle
          cx={dotX}
          cy={dotY}
          r={4}
          fill="#000"
        />

        {/* Label box with content inside */}
        <foreignObject
          x={boxX}
          y={boxY}
          width={boxWidth}
          height={boxHeight}
        >
          <div
            style={{
              backgroundColor: '#1A1C31',
              color: 'white',
              borderRadius: '8px',
              padding: '6px 8px',
              fontSize: '14px',
              fontWeight: 'bold',
              textAlign: 'center',
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              boxSizing: 'border-box'
            }}
          >
            <div style={{
              fontSize: '12px',
              fontWeight: '100', opacity: '0.85'
            }}>{payload.name}</div>
            <div style={{ fontSize: '16px', fontWeight: '600' }}>
              {payload.value.toLocaleString()}
            </div>
          </div>
        </foreignObject>
      </g>
    );
  };


  return (
    <div className="-m-6 bg-[#f1f3f6] font-poppins">
      <div className="px-6 pt-6">
        <section className="bg-white p-4 rounded-xl flex items-center gap-4">
          <img
            className="h-14 w-14 rounded-full object-cover"
            src={profilePictureUrl || "/images/avatar.png"}
            alt=""
            onError={(e) => {
              e.currentTarget.src = "/images/avatar.png";
            }}
          />
          <div>
            <p className="text-primaryblue-100 text-xs">Welcome Back!</p>
            <p className="mt-1 text-primaryblue-100 text-xl font-bold">
              {username ? `${username.charAt(0).toUpperCase()}${username.slice(1)}` : 'UserName'}
            </p>

          </div>
        </section>

        <section className="grid md:grid-cols-12 gap-6 mt-6">
          {/* Cards go 3-across only at xl — inside md:col-span-8 a 3-column row
              at tablet width leaves each card too narrow for its label. */}
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* h-full on both the link and the card so all three stay the same
                height whatever their label/value lengths. shrink-0 keeps the
                icon and arrow at full size and lets only the text column give. */}
            <Link to="/admin/load-posted" className="block h-full">
              <div className="bg-white p-4 rounded-xl flex items-center justify-between gap-4 h-full">
                <div className="flex gap-4 items-center min-w-0">
                  <div className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-violet-100">
                    <img className="w-5 h-5" src="/images/seat.svg" alt="" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-light tracking-wide">Total Loads Posted</p>
                    <p className="mt-1 text-grease-100 text-xl font-bold">{dashboardData?.loadPostedCount ?? 0}</p>
                  </div>
                </div>
                <i className="st-arrow-left rotate-180 text-xs text-royalblue-100 shrink-0"></i>
              </div>
            </Link>
            <Link to="/admin/load-answered" className="block h-full">
              <div className="bg-white p-4 rounded-xl flex items-center justify-between gap-4 h-full">
                <div className="flex gap-4 items-center min-w-0">
                  <div className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-skyblue-100">
                    <img className="w-5 h-5" src="/images/seat.svg" alt="" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-light tracking-wide">Total Loads Answered</p>
                    <p className="mt-1 text-grease-100 text-xl font-bold">{dashboardData?.loadAnsweredCount ?? 0}</p>
                  </div>
                </div>
                <i className="st-arrow-left rotate-180 text-xs text-royalblue-100 shrink-0"></i>
              </div>
            </Link>
            <Link to="/admin/load-unanswered" className="block h-full">
              <div className="bg-white p-4 rounded-xl flex items-center justify-between gap-4 h-full">
                <div className="flex gap-4 items-center min-w-0">
                  <div className="flex items-center justify-center h-10 w-10 shrink-0 rounded-full bg-brown-100">
                    <img className="w-5 h-5" src="/images/seat.svg" alt="" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 font-light tracking-wide">Total Loads Unanswered</p>
                    <p className="mt-1 text-grease-100 text-xl font-bold">{dashboardData?.loadUnansweredCount ?? 0}</p>
                  </div>
                </div>
                <i className="st-arrow-left rotate-180 text-xs text-royalblue-100 shrink-0"></i>
              </div>
            </Link>
            <div className="bg-white rounded-xl sm:col-span-2 xl:col-span-3 p-4">
              <div className="flex items-center justify-between gap-4 mb-4">
                <p className="text-sm text-grease-100 font-semibold">Loads Trend</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="bg-violet-100 h-3 w-3 rounded-full"></div>
                    <p className="text-gray-500 text-xs">Posted</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="bg-skyblue-100 h-3 w-3 rounded-full"></div>
                    <p className="text-gray-500 text-xs">Answered</p>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData} barGap={8} margin={{ bottom: 12 }}>
                  <CartesianGrid
                    strokeDasharray="0"
                    horizontal={true}
                    vertical={false}
                    stroke="#F0F0F0"
                  />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={48}
                    tickMargin={8}
                    tick={{ fill: '#A0AEC0', fontSize: 10 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#A0AEC0', fontSize: 12 }}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="Posted"
                    fill="rgb(95 112 228 / 1)"
                    radius={[10, 10, 10, 10]}
                    barSize={14}
                  />
                  <Bar
                    dataKey="Answered"
                    fill="rgb(95 210 228 / 1)"
                    radius={[10, 10, 10, 10]}
                    barSize={14}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="md:col-span-4 bg-white rounded-xl p-3">
            <div className="flex items-center justify-between mb-16">
              <p className="text-sm text-grease-100 font-semibold">Load Distribution</p>
            </div>
            <div className="flex justify-center items-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                  <Pie
                    data={loadDistributionData}
                    cx="50%"
                    cy="50%"
                    outerRadius={85}
                    fill="#8884d8"
                    dataKey="value"
                    animationBegin={0}
                    animationDuration={500} // Reduce animation duration for smoother experience
                    animationEasing="ease-out" // Use 'ease-out' for a smoother finish
                    isAnimationActive={true} // Disable animation to improve performance
                    label={renderCustomizedLabel}
                  >
                    <Cell fill="rgb(95 112 228 / 1)" />
                    <Cell fill="rgb(95 210 228 / 1)" />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
